import { pageMixin } from '../mixins/signup'
import FormResult from '../components/FormResult'
import SignupForm from './SignupForm'
import dayjs from 'dayjs'

export default {
  name: "signup-form",
  mixins: [pageMixin],
  components: {
    FormResult,
    SignupForm
  },
  props: {
    links: {
      type: Object,
      default: () => ({
        terms: "/terms-and-conditions",
        privacy: "/privacy-policy"
      })
    }
  },
  data() {
    return {
      formValues: {
        'salutation': {
          'name': '',
          'display': ''
        },
        'firstname': '',
        'lastname': '',
        'email': '',
        'dob': '',
        shipping: {
          'firstname': '',
          'lastname': '',
          'street1': '',
          'street2': '',
          'city': '',
          'state': '',
          'country': '',
          'zip': ''
        },
        billing: {
          'firstname': '',
          'lastname': '',
          'street1': '',
          'street2': '',
          'city': '',
          'state': '',
          'country': '',
          'zip': ''
        },
        'mobile': {
          'countryCode': 'AU',
          'isValid': false,
          'phoneNumber': '',
          'countryCallingCode': '',
          'formattedNumber': '',
          'nationalNumber': '',
          'formatInternational': '',
          'formatNational': '',
          'uri': '',
          'e164': ''
        },
        'communicationPreferences': [
          {
            label: 'Allow us to contact you via email',
            value: true
          },
          {
            label: 'Allow us to contact you via phone',
            value: true
          }
        ]
      },
      requestValues: {
        person: {
          email: '',
          mobile: {
            country_code: 'AU',
            number: ''
          },
          name: {
            firstname: '',
            lastname: ''
          }
        },
        addresses: {
          addresstypes: {},
          addresslist: []
        },
        comms: {
          email_ok: true,
          call_ok: true
        }
      },
      showVerification: false,
      recaptcha: {
        verified: false,
        previouslyCompleted: false,
        response: null
      }
    }
  },
  mounted() {
    this.loadCountries()
  },
  computed: {
    formOptions() {
      return this.$store.state.registry.getModuleSetting('signup-page', 'form-options')
    },
    acceptedCountries() {
      const countryData = this.$store.getters['ecommerce/getConfigItem'](
        'address',
        'allowed_countries'
      )
      const allowedCountries = []
      Object.entries(countryData).forEach(([key, value]) => {
        const out = { name: key, display: value }
        allowedCountries.push(out)
      })
      return allowedCountries
    },
    emailAddressIsValid() {
      return this.validateEmailAddress();
    },
    firstNameIsValid() {
      return this.requestValues.person.name.firstname !== ''
    },
    lastNameIsValid() {
      return this.requestValues.person.name.lastname !== ''
    },
    userDetailsAreValid() {
      return this.firstNameIsValid && this.lastNameIsValid && this.emailAddressIsValid && this.recaptchaCompleted
    },
    recaptchaIsVerified() {
      return this.recaptcha.verified
    },
    recaptchaCompleted() {
      return this.recaptchaIsVerified && this.recaptcha.response !== null
    },
    userLoggedIn() {
      return this.$store.getters["user/userid"] || false;
    },
    recaptchaKey: function() {
      try {
        const recaptchaKey = this.$store.getters['getModuleSetting']('captcha', 'recaptchav2-key') ?? ''
        return recaptchaKey
      }
      catch (err) {
        console.error(err)

        return ''
      }
    },
    defaultCountryCode() {
      const countryCode = this.$store.getters['ecommerce/getConfigItem']('address', 'default_country') || 'AU'

      return countryCode.toUpperCase()
    }
  },
  watch: {
    'formValues.firstname': {
      handler: 'mapFirstName',
    },
    'formValues.lastname': {
      handler: 'mapLastName',
    },
    'formValues.dob': {
      handler: 'mapDOB',
    },
    'formValues.email': {
      handler: 'mapEmail',
    },
    'formValues.mobile': {
      handler: 'mapPhoneNumber',
      deep: true
    },
    'formValues.communicationPreferences': {
      handler: 'mapCommuncationPreferences',
      deep: true
    },
    'formValues.shipping': {
      handler: 'mapShipping',
      deep: true
    },
    'formValues.billing': {
      handler: 'mapBilling',
      deep: true
    }
  },
  methods: {
    mapFirstName(payload) {
      this.$set(this.requestValues.person.name, 'firstname', payload)
    },
    mapLastName(payload) {
      this.$set(this.requestValues.person.name, 'lastname', payload)
    },
    mapEmail(payload) {
      this.$set(this.requestValues.person, 'email', payload)
    },
    mapDOB(payload) {
      this.$set(this.requestValues.person, 'dateofbirth', dayjs(payload).format('YYYY-MM-DDTHH:mm:ssZ'))
    },
    mapPhoneNumber(payload) {
      this.$set(this.requestValues.person.mobile, 'country_code', payload.countryCode)
      this.$set(this.requestValues.person.mobile, 'number', payload.phoneNumber ?? '')
    },
    mapShipping(payload) {
      this.mapAddress(payload, 'delivery')
    },
    mapBilling(payload) {
      this.mapAddress(payload, 'physical')
    },
    mapAddress(payload, type) {
      let index = this.requestValues.addresses.addresslist.length
      if (this.requestValues.addresses.addresstypes[type]) {
        index = this.requestValues.addresses.addresstypes[type]
      }
      this.$set(this.requestValues.addresses.addresstypes, type, index)
      this.$set(this.requestValues.addresses.addresslist, index, {
        street1: payload.street1,
        street2: payload.street2,
        city: payload.city,
        state: payload.state,
        country: payload.country,
        postcode: payload.zip
      })
    },
    mapCommuncationPreferences(payload) {
      this.mapEmailCommunicationPreference(payload[0])
      this.mapPhoneCommunicationPreference(payload[1])
    },
    mapEmailCommunicationPreference(payload) {
      this.$set(this.requestValues.comms, 'email_ok', payload.value)
    },
    mapPhoneCommunicationPreference(payload) {
      this.$set(this.requestValues.comms, 'call_ok', payload.value)
    },
    validateEmailAddress() {
      const email = this.requestValues.person.email
      const notEmpty = email !== ''
      const regexPattern = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
      const regexPassed = regexPattern.test(String(email).toLowerCase())

      return notEmpty && regexPassed
    },
    resetForm() {
      this.signed = null;
    },
    recaptchaVerificationHandler(payload) {
      this.updateRecaptchaResponse(payload)
      this.flagRecaptchaAsVerified()
      this.flagRecaptchaCompletion()
    },
    recaptchaExpirationHandler() {
      this.invalidateRecaptchaVerfication()
    },
    invalidateRecaptchaVerification() {
      this.$set(this.recaptcha, 'verified', false)
      this.updateRecaptchaToken('')
    },
    updateRecaptchaResponse(payload) {
      this.$set(this.recaptcha, 'response', payload)
    },
    flagRecaptchaAsVerified() {
      this.$set(this.recaptcha, 'verified', true)
    },
    flagRecaptchaCompletion() {
      this.$set(this.recaptcha, 'previouslyCompleted', true)
    },
    submitSignup() {
      if (this.userDetailsAreValid) {
        this.$store.dispatch('showProgressLoader')
          .then(_ => this.signupUser(this.requestValues))
          .finally(_ => this.$store.dispatch('hideProgressLoader'))
      }
    }
  }
}
